import React from 'react';
import { InvoiceStatus } from '@/types/invoice.types';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<InvoiceStatus, { label: string; colors: string }> = {
  DRAFT: {
    label: 'Draft',
    colors: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  PENDING: {
    label: 'Pending',
    colors: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  PARTIAL: {
    label: 'Partial',
    colors: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  PAID: {
    label: 'Paid',
    colors: 'bg-green-100 text-green-800 border-green-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    colors: 'bg-red-100 text-red-800 border-red-200',
  },
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  size = 'md',
  className = '',
}) => {
  const config = statusConfig[status] || statusConfig.PENDING;

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded-full border
        ${config.colors}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {config.label}
    </span>
  );
};

export const getInvoiceStatusLabel = (status: InvoiceStatus): string => {
  return statusConfig[status]?.label || status;
};

export const getAllInvoiceStatuses = (): { value: InvoiceStatus; label: string }[] => {
  return Object.entries(statusConfig).map(([value, { label }]) => ({
    value: value as InvoiceStatus,
    label,
  }));
};

export default InvoiceStatusBadge;
